/**
 * CORP//LLM — Updates Panel (admin-pushed notifications)
 * Different from in-game alerts (fixture.notifications):
 * - persistent (stored in DB)
 * - cross-session
 * - pushed via WS event `notification_new` + REST list
 * - marked read per-player
 */

import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../api';
import type { Notification as ApiNotification } from '../api';
import { t } from '../i18n';

interface UpdatesCache {
  items: ApiNotification[];
  unread: number;
}

let cache: UpdatesCache = { items: [], unread: 0 };
let loadInFlight: Promise<void> | null = null;

export function getUpdatesCache(): UpdatesCache {
  return cache;
}

export async function loadUpdates(): Promise<void> {
  if (loadInFlight) return loadInFlight;
  loadInFlight = (async () => {
    try {
      const resp = await listNotifications();
      cache = { items: resp.notifications ?? [], unread: resp.unreadCount ?? 0 };
      renderUpdatesBadge();
      renderUpdatesList();
    } catch {
      // swallow — badge stays at previous value
    } finally {
      loadInFlight = null;
    }
  })();
  return loadInFlight;
}

export function renderUpdatesBadge(): void {
  const badge = document.getElementById('updatesBadge');
  if (!badge) return;
  badge.textContent = cache.unread > 99 ? '99+' : String(cache.unread);
  badge.classList.toggle('visible', cache.unread > 0);
}

function formatTimeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const secs = Math.max(0, Math.floor((now - then) / 1000));
  if (secs < 60)   return t('updates.time.now');
  if (secs < 3600) return t('updates.time.min', { n: String(Math.floor(secs / 60)) });
  if (secs < 86400) return t('updates.time.hour', { n: String(Math.floor(secs / 3600)) });
  return t('updates.time.day', { n: String(Math.floor(secs / 86400)) });
}

function escapeHTML(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default:  return '&#39;';
    }
  });
}

function isSafeLink(u: string | undefined): u is string {
  if (!u) return false;
  try {
    const parsed = new URL(u);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function renderUpdatesList(): void {
  const list = document.getElementById('updatesList');
  if (!list) return;
  list.innerHTML = '';

  if (cache.items.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'update-empty';
    empty.textContent = t('updates.empty');
    list.appendChild(empty);
    return;
  }

  for (const n of cache.items) {
    const item = document.createElement('div');
    item.className = 'update-item' + (n.read ? '' : ' unread');
    item.dataset.id = n.id;

    const titleSafe = escapeHTML(n.title);
    const bodySafe  = escapeHTML(n.body);
    const linkOk    = isSafeLink(n.linkUrl);
    const linkLabel = n.linkLabel ? escapeHTML(n.linkLabel) : t('updates.link');
    const linkHTML  = linkOk
      ? `<a class="update-link" href="${escapeHTML(n.linkUrl!)}" target="_blank" rel="noopener noreferrer">${linkLabel}</a>`
      : '';

    const metaBits: string[] = [];
    if (n.isGlobal) metaBits.push(`<span>${escapeHTML(t('updates.global'))}</span>`);
    metaBits.push(`<span>${escapeHTML(formatTimeAgo(n.createdAt))}</span>`);

    item.innerHTML = `
      <div class="update-dot ${n.severity}"></div>
      <div class="update-body">
        <div class="update-title">${titleSafe}</div>
        <div class="update-text">${bodySafe}</div>
        <div class="update-meta">
          ${metaBits.join('')}
          ${linkHTML}
        </div>
      </div>`;

    if (!n.read) {
      item.addEventListener('click', () => {
        void markOne(n.id);
      });
    }

    list.appendChild(item);
  }
}

async function markOne(id: string): Promise<void> {
  try {
    await markNotificationRead(id);
    const hit = cache.items.find(x => x.id === id);
    if (hit && !hit.read) {
      hit.read = true;
      cache.unread = Math.max(0, cache.unread - 1);
    }
    renderUpdatesBadge();
    renderUpdatesList();
  } catch {
    // leave cache unchanged; next load() reconciles
  }
}

export async function markAllRead(): Promise<void> {
  try {
    await markAllNotificationsRead();
    for (const it of cache.items) it.read = true;
    cache.unread = 0;
    renderUpdatesBadge();
    renderUpdatesList();
  } catch {
    // ignore
  }
}

export function initUpdates(): void {
  const btn    = document.getElementById('updatesBtn')   as HTMLButtonElement | null;
  const panel  = document.getElementById('updatesPanel') as HTMLElement | null;
  const close  = document.getElementById('updatesClose') as HTMLButtonElement | null;
  const markAll = document.getElementById('updatesMarkAll') as HTMLButtonElement | null;

  if (!btn || !panel) return;

  btn.addEventListener('click', () => {
    const opening = !panel.classList.contains('open');
    panel.classList.toggle('open');
    if (opening) void loadUpdates();
  });
  close?.addEventListener('click', () => panel.classList.remove('open'));
  markAll?.addEventListener('click', (e) => {
    e.stopPropagation();
    void markAllRead();
  });

  renderUpdatesBadge();
  void loadUpdates();
}

/**
 * Called by WS `notification_new`. The server sends only a small hint
 * (id/severity/title), not the full row — so we bump the badge for
 * instant feedback and reconcile via loadUpdates().
 */
export function onNotificationPush(_hint?: { id?: string; severity?: string; title?: string; global?: boolean }): void {
  cache.unread += 1;
  renderUpdatesBadge();
  void loadUpdates();
}
